import { some } from 'lodash';
import { z } from 'zod';

export const RestaurantBookingSchema = z.object({
  restaurant: z.object({
    configName: z.string(),
    restaurantName: z.string(),
    id: z.number(),
  }),
  adults: z.number(),
  children: z.number().optional(),
  dayNumber: z.number(),
  visitDateRangeStart: z.string().optional(),
  visitDate: z.string(),
  timeslot: z.object({
    availablePromotions: z
      .array(
        z.object({
          basePrice: z.number().optional(),
          feeAmount: z.number().optional(),
          id: z.string().or(z.number()),
          leaveTime: z.string(),
        })
      )
      .nullable(),
    hasStandardAvailability: z.boolean(),
    isLeaveTimeRequired: z.boolean().nullable(),
    leaveTime: z.string().nullable(),
    serviceId: z.number().nullable(),
    standardAvailabilityFeeAmount: z.number().nullable(),
    timeSlot: z.string(),
    timeslotReservationId: z.string().nullable().optional(),
  }),
  promotion: z.object({
    id: z.string().nullable(),
    name: z.string().nullable(),
    timeslotReservationId: z.string().nullable().optional(),
  }),
  hasDietaryRequirements: z.boolean().optional().default(false),
  dietaryRequirements: z.array(z.string()).optional(),
  hasCelebrationOptions: z.boolean().optional().default(false),
  celebrationOptions: z.array(z.string()).optional(),
});

export const TablesSchema = z
  .object({
    restaurants: z.array(RestaurantBookingSchema).optional(),
    notes: z.string().optional(),
  })
  .optional()
  .superRefine((data, ctx) => {
    if (!data?.restaurants) return;

    data.restaurants.forEach((restaurant, index) => {
      if (restaurant.hasDietaryRequirements && !some(restaurant.dietaryRequirements)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['restaurants', index, 'dietaryRequirements'],
        });
      }

      if (restaurant.hasCelebrationOptions && !some(restaurant.celebrationOptions)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['restaurants', index, 'celebrationOptions'],
        });
      }
    });
  });

export type RestaurantBooking = z.infer<typeof RestaurantBookingSchema>;
export type TablesFormValues = z.infer<typeof TablesSchema>;

export const PackageRestaurantSchema = z.object({
  delayAfter: z.number().optional(),
  id: z.string().optional(),
  menuIds: z.array(z.string()),
  restaurantId: z.string(),
  name: z.string().optional().nullable(),
  restaurantName: z.string().optional().nullable(),
  timeRestrictions: z
    .object({ startTime: z.string().length(8), endTime: z.string().length(8) })
    .nullable(),
  dayNumber: z.number(),
  dayNumberRange: z.array(z.number()),
  additionalRestaurants: z
    .array(
      z.object({
        restaurantId: z.string(),
        menuIds: z.array(z.string()),
        timeRestrictions: z
          .object({ startTime: z.string().length(8), endTime: z.string().length(8) })
          .nullable()
          .optional(),
      })
    )
    .optional(),
});

export type PackageRestaurant = z.infer<typeof PackageRestaurantSchema>;

export type DiningRestaurantChoice = {
  id: number;
  menuIds: (string | number)[];
  timeslotRestrictions?: { startTime: string; endTime: string } | null;
};

export type DiningOption = DiningRestaurantChoice & {
  restaurantChoices?: DiningRestaurantChoice[];
  name?: string;
  delayAfter?: number;
  dayNumber: number;
  dayNumberRange: number[];
};
